import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { useCurrentUser } from '../auth';
import { computeDisplayName, type AccountLink, type Contact } from '../../types/contact';
import { listContacts, updateContact } from './api';
import { LinkExistingContactModal } from './LinkExistingContactModal';
import { ContactAvatar } from './ContactAvatar';

// Contacts section embedded on the Account detail page. Scoped by
// accountId via the existing ?accountId=X filter on listContacts (which
// translates to a Firestore where('accountIds', 'array-contains', X)).
//
// Role gating: staff users see the list but not the Add/Link buttons
// (contact CRUD + linking is admin-only, matching the ContactList page).

function linkForAccount(contact: Contact, accountId: string): AccountLink | undefined {
  return contact.accountLinks.find((l) => l.accountId === accountId);
}

function CommsPills({ link }: { link: AccountLink }) {
  const pills: Array<{ label: string; on: boolean; color: string }> = [
    {
      label: 'Service',
      on: link.receivesServiceCommunications,
      color: 'bg-emerald-100 text-emerald-700',
    },
    {
      label: 'Billing',
      on: link.receivesBillingCommunications,
      color: 'bg-amber-100 text-amber-700',
    },
    {
      label: 'Campaign previews',
      on: link.receivesCampaignPreviews,
      color: 'bg-indigo-100 text-indigo-700',
    },
  ];
  const active = pills.filter((p) => p.on);
  if (active.length === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {active.map((p) => (
        <span key={p.label} className={`px-2 py-0.5 rounded text-xs font-medium ${p.color}`}>
          {p.label}
        </span>
      ))}
    </div>
  );
}

export function AccountContactsSection({ accountId }: { accountId: string }) {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const contactsQuery = useQuery({
    queryKey: ['contacts', accountId],
    queryFn: () => listContacts(accountId),
  });

  const unlinkMutation = useMutation({
    mutationFn: async (contact: Contact) => {
      const nextLinks = contact.accountLinks.filter((l) => l.accountId !== accountId);
      return updateContact(contact.id, { accountLinks: nextLinks });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', accountId] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const isAdmin = user.role === 'admin';
  const linkedContacts = contactsQuery.data ?? [];

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex gap-2 justify-end">
          <Link to={`/contacts/new?account=${encodeURIComponent(accountId)}`}>
            <Button type="button">Add contact</Button>
          </Link>
          <Button
            type="button"
            variant="outline"
            onClick={() => setLinkModalOpen(true)}
          >
            Link existing contact
          </Button>
        </div>
      )}

      {contactsQuery.isLoading && <CenteredSpinner />}
      {contactsQuery.error && (
        <div className="text-sm text-red-600">
          Failed to load contacts: {(contactsQuery.error as Error).message}
        </div>
      )}

      {contactsQuery.data && linkedContacts.length === 0 && (
        <p className="text-sm text-slate-500">
          No contacts linked yet.{' '}
          {isAdmin ? 'Use the buttons above to add or link someone.' : 'Ask an admin to add one.'}
        </p>
      )}

      {linkedContacts.length > 0 && (
        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Role / Title</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">Primary</th>
                <th className="text-left px-4 py-2">Communications</th>
                {isAdmin && <th className="text-right px-4 py-2">&nbsp;</th>}
              </tr>
            </thead>
            <tbody>
              {linkedContacts.map((c) => {
                const link = linkForAccount(c, accountId);
                if (!link) return null;
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link
                        to={`/contacts/${c.id}`}
                        className="flex items-center gap-2 text-slate-900 hover:underline"
                      >
                        <ContactAvatar contact={c} size="sm" decorative />
                        <span>{computeDisplayName(c)}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{link.jobTitle ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-700 font-mono text-xs break-all">
                      <div className="flex items-center gap-2">
                        <span>{c.email ?? '—'}</span>
                        {c.channelEmail && c.email !== null && (
                          <span
                            className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"
                            title="Opted in to email previews"
                            aria-label="Email channel on"
                          >
                            📧
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-slate-700 font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span>{c.phone ?? '—'}</span>
                        {c.channelSMS && c.phone !== null && (
                          <span
                            className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700"
                            title="Opted in to SMS previews"
                            aria-label="SMS channel on"
                          >
                            📱
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {link.isPrimary ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700">
                          Primary
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <CommsPills link={link} />
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove ${computeDisplayName(c)} from this account? The contact itself isn't deleted — only the link is removed.`,
                              )
                            ) {
                              unlinkMutation.mutate(c);
                            }
                          }}
                          disabled={unlinkMutation.isPending}
                        >
                          Unlink
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {linkModalOpen && (
        <LinkExistingContactModal
          accountId={accountId}
          alreadyLinkedIds={new Set(linkedContacts.map((c) => c.id))}
          onClose={() => setLinkModalOpen(false)}
          onLinked={() => {
            setLinkModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['contacts', accountId] });
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
          }}
        />
      )}
    </div>
  );
}
